/**
 * Game Screen — Multiplayer Live-Race.
 *
 * Empfängt Puzzle vom Server, sendet Antwort zurück.
 * Server entscheidet Korrektheit + Zeit (anti-cheat).
 */
import { div, p, button, el } from '../../utils/domHelpers.js';
import { router } from '../../core/router.js';
import { lobbyService } from '../../services/lobbyService.js';
import { eventBus } from '../../core/eventBus.js';
import { createNumpad, bindNumpadKeyboard } from '../components/numpad.js';
import { toast } from '../components/toast.js';
import { getCategoryLabel, getDifficultyLabel, formatTime } from '../../utils/formatters.js';

export function renderGameScreen() {
  let currentAnswer = '';
  let usedHint = false;
  let timerInterval, timerEnd;

  let roundEl, totalRoundsEl, scoreEl, timerFill, puzzleEl, answerDisplay, hintEl;
  let unbindKb = null;

  // === Helpers ===
  const updateAnswer = () => { answerDisplay.textContent = currentAnswer || '_'; };
  const appendDigit  = (d) => { if (currentAnswer.length < 10) { currentAnswer += d; updateAnswer(); } };
  const deleteDigit  = () => { currentAnswer = currentAnswer.slice(0, -1); updateAnswer(); };

  const submit = () => {
    if (currentAnswer.length === 0) return toast.error('Antwort eingeben');
    lobbyService.submitAnswer(currentAnswer, usedHint);
    answerDisplay.classList.add('pulse-glow');
  };

  const requestHint = () => { usedHint = true; lobbyService.useHint(); };

  // === Server-Events ===
  const handleRoundStart = (msg) => {
    currentAnswer = ''; usedHint = false; updateAnswer();
    answerDisplay.classList.remove('pulse-glow', 'flash-ok', 'flash-err', 'shake');
    hintEl.textContent = '';

    roundEl.textContent = msg.round;
    totalRoundsEl.textContent = msg.totalRounds;

    puzzleEl.innerHTML = '';
    puzzleEl.appendChild(div({ class: 'flex gap-2 flex-center', style: { marginBottom: '12px' } }, [
      el('span', { class: 'badge' }, [getCategoryLabel(msg.puzzle.cat)]),
      el('span', { class: 'badge badge--magenta' }, [getDifficultyLabel(msg.puzzle.diff)]),
      el('span', { class: 'badge badge--success' }, [`Runde ${msg.round}/${msg.totalRounds}`]),
    ]));
    puzzleEl.appendChild(el('div', { class: 'puzzle-display' }, [msg.puzzle.q]));

    startTimer(msg.timeoutMs);
  };

  const handleAnswerResult = (msg) => {
    answerDisplay.classList.remove('pulse-glow');
    answerDisplay.classList.add(msg.correct ? 'flash-ok' : 'flash-err');
    if (!msg.correct) answerDisplay.classList.add('shake');
    scoreEl.textContent = msg.totalScore;
    const bonusTxt = msg.bonus ? ` (+${msg.bonus} Bonus!)` : '';
    toast[msg.correct ? 'success' : 'error'](
      msg.correct ? `✓ +${msg.points}${bonusTxt}` : '✗ Falsch'
    );
  };

  const handleRoundEnd = (msg) => {
    stopTimer();
    toast.info(`Lösung: ${msg.correctAnswer}`);
  };

  const handleGameEnd = (msg) => {
    sessionStorage.setItem('mpa:lastRanking', JSON.stringify(msg.ranking));
    router.navigate('/results');
  };

  const handleHint = (msg) => { hintEl.textContent = `💡 ${msg.hint}`; };

  // === Timer ===
  function startTimer(timeoutMs) {
    timerEnd = Date.now() + timeoutMs;
    timerFill.style.width = '100%';
    timerFill.classList.remove('timer-bar__fill--warn');
    if (timerInterval) clearInterval(timerInterval);

    timerInterval = setInterval(() => {
      const remaining = Math.max(0, timerEnd - Date.now());
      const pct = (remaining / timeoutMs) * 100;
      timerFill.style.width = pct + '%';
      if (pct < 25) timerFill.classList.add('timer-bar__fill--warn');
      if (remaining <= 0) stopTimer();
    }, 100);
  }
  function stopTimer() { if (timerInterval) { clearInterval(timerInterval); timerInterval = null; } }

  // === Subscriptions ===
  const offs = [
    eventBus.on('server:round_started', handleRoundStart),
    eventBus.on('server:answer_result', handleAnswerResult),
    eventBus.on('server:round_ended',   handleRoundEnd),
    eventBus.on('server:game_ended',    handleGameEnd),
    eventBus.on('server:hint_revealed', handleHint),
  ];

  eventBus.on('route:before-change', () => {
    offs.forEach(off => off());
    stopTimer();
    unbindKb?.();
  });

  // === Layout ===
  roundEl       = el('span', { class: 'hud-value mono' }, ['1']);
  totalRoundsEl = el('span', { class: 'mono' }, ['5']);
  scoreEl       = el('span', { class: 'hud-value mono' }, ['0']);
  timerFill     = el('div',  { class: 'timer-bar__fill', style: { width: '100%' } });
  puzzleEl      = el('div',  { class: 'flex-col flex-center w-full' });
  answerDisplay = el('div',  { class: 'answer-display' }, ['_']);
  hintEl        = el('p',    { class: 'mono', style: { color: 'var(--magenta)', minHeight: '24px', textAlign: 'center' } });

  const numpad = createNumpad({ onInput: appendDigit, onDelete: deleteDigit, onSubmit: submit });
  unbindKb = bindNumpadKeyboard({ onInput: appendDigit, onDelete: deleteDigit, onSubmit: submit });

  return el('div', { class: 'glass-panel glass-panel--wide fade-up' }, [
    div({ class: 'game-hud' }, [
      div([ el('span', { class: 'hud-item' }, ['Runde ']), roundEl,
            el('span', { class: 'hud-item' }, ['/']), totalRoundsEl ]),
      div([ el('span', { class: 'hud-item' }, ['Score ']), scoreEl ]),
      button({ class: 'btn btn-ghost btn-sm', onclick: () => { lobbyService.leaveLobby(); router.navigate('/'); } }, ['Verlassen']),
    ]),

    div({ class: 'timer-bar' }, [timerFill]),

    puzzleEl,
    answerDisplay,
    hintEl,
    numpad,

    div({ class: 'flex-center', style: { marginTop: '12px' } }, [
      button({ class: 'btn btn-ghost btn-sm', onclick: requestHint }, ['💡 Hint (−100)']),
    ]),
  ]);
}
